import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseService } from '../../../shared/services/base.service';
import { ApiResponse } from '../../../shared/model/api-response';
import { AddressDto, GeoLocation } from '../../../shared/model/geoLocation';
import { GeoCoordinates, parseCoordinates } from '../../../shared/utils/geo-coordinates';

@Injectable({
  providedIn: 'root',
})
export class GeoLocationService extends BaseService {
  private static readonly UNDEFINED_LOCATION_LABEL = 'Luogo da definire';

  private httpClient: HttpClient = inject(HttpClient);

  public getGeolocation(address: string): Observable<ApiResponse<GeoLocation[]>> {
    const params = new HttpParams().set('address', address);
    return this.httpClient.get<ApiResponse<GeoLocation[]>>(`${this.baseUrl}/location`, { params });
  }

  public getMyLocations(formalName: string): Observable<ApiResponse<GeoLocation[]>> {
    const params = new HttpParams().set('formalName', formalName);
    return this.httpClient.get<ApiResponse<GeoLocation[]>>(`${this.baseUrl}/location/mine`, {
      params,
    });
  }

  public getAllLocations(): Observable<ApiResponse<GeoLocation[]>> {
    return this.httpClient.get<ApiResponse<GeoLocation[]>>(`${this.baseUrl}/location/all`);
  }

  public updateLocation(id: number, location: Partial<GeoLocation>): Observable<ApiResponse<GeoLocation>> {
    return this.httpClient.put<ApiResponse<GeoLocation>>(`${this.baseUrl}/location/${id}`, location);
  }

  public deleteLocation(id: number): Observable<ApiResponse<void>> {
    return this.httpClient.delete<ApiResponse<void>>(`${this.baseUrl}/location/${id}`);
  }

  public toApiLocation(location?: unknown): GeoLocation | null {
    const loc = this.normalizeLocation(location) as GeoLocation | undefined;
    if (!loc) {
      return null;
    }

    // Explicitly grab ID from the raw object to make sure it's not lost
    let idValue: number | undefined;

    // Check if it's a form group
    const rawLoc = location as any;
    const formId = rawLoc?.get ? rawLoc.get('id')?.value : undefined;
    const rawId = formId !== undefined ? formId : rawLoc?.id;

    if (rawId != null && String(rawId).trim() !== '') {
        idValue = Number(rawId);
    } else if (loc.id != null && String(loc.id).trim() !== '') {
        idValue = Number(loc.id);
    }
    const id = idValue != null && Number.isFinite(idValue) ? idValue : undefined;
    const formalName = (loc.formalName || '').trim();
    const displayName = (loc.displayName || loc.rawInput || '').trim();
    const lat = (loc.lat || (loc.latitude != null ? `${loc.latitude}` : '')).trim();
    const lon = (loc.lon || (loc.longitude != null ? `${loc.longitude}` : '')).trim();
    const nested = this.addressAsObject(loc.address);

    const address: AddressDto = {
      street: emptyToUndefined(loc.street || nested?.street),
      houseNumber: emptyToUndefined(loc.houseNumber || nested?.houseNumber),
      city: emptyToUndefined(loc.city || nested?.city),
      province: emptyToUndefined(loc.province || nested?.province),
      country: emptyToUndefined(loc.country || nested?.country),
      town: emptyToUndefined(loc.town || nested?.town),
      village: emptyToUndefined(loc.village || nested?.village),
      neighbourhood: emptyToUndefined(loc.neighbourhood || nested?.neighbourhood),
      residential: emptyToUndefined(loc.residential || nested?.residential),
      postcode: emptyToUndefined(loc.postcode || nested?.postcode),
    };
    const hasAddress = Object.values(address).some((value) => !!value);

    if (!id && !formalName && !displayName && !lat && !lon && !hasAddress) {
      return null;
    }

    return {
      ...(id ? { id } : {}),
      formalName: formalName || undefined,
      displayName: displayName || undefined,
      lat: lat || undefined,
      lon: lon || undefined,
      address: hasAddress ? address : undefined,
    };
  }

  public normalizeLocation(location?: unknown): unknown {
    if (!location) {
      return location;
    }

    if (typeof location === 'string') {
      return { formalName: location, address: location, rawInput: location };
    }

    const asLocation = location as Record<string, unknown>;
    const address = asLocation['address'] as Record<string, unknown> | string | undefined;
    const addressObj =
      typeof address === 'object' && address !== null ? (address as Record<string, unknown>) : {};

    return {
      ...asLocation,
      address,
      city:
        (asLocation['city'] as string | undefined) ?? (addressObj['city'] as string | undefined),
      country:
        (asLocation['country'] as string | undefined) ??
        (addressObj['country'] as string | undefined),
      town:
        (asLocation['town'] as string | undefined) ?? (addressObj['town'] as string | undefined),
      village:
        (asLocation['village'] as string | undefined) ??
        (addressObj['village'] as string | undefined),
      neighbourhood:
        (asLocation['neighbourhood'] as string | undefined) ??
        (addressObj['neighbourhood'] as string | undefined),
      residential:
        (asLocation['residential'] as string | undefined) ??
        (addressObj['residential'] as string | undefined),
      postcode:
        (asLocation['postcode'] as string | undefined) ??
        (addressObj['postcode'] as string | undefined),
      street:
        (asLocation['street'] as string | undefined) ??
        (addressObj['street'] as string | undefined),
      houseNumber:
        (asLocation['houseNumber'] as string | undefined) ??
        (addressObj['houseNumber'] as string | undefined),
      province:
        (asLocation['province'] as string | undefined) ??
        (addressObj['province'] as string | undefined),
      latitude:
        (asLocation['latitude'] as number | undefined) ??
        (asLocation['lat'] ? Number(asLocation['lat']) : undefined),
      longitude:
        (asLocation['longitude'] as number | undefined) ??
        (asLocation['lon'] ? Number(asLocation['lon']) : undefined),
      lat: asLocation['lat'],
      lon: asLocation['lon'],
      rawInput:
        (asLocation['rawInput'] as string | undefined) ??
        (typeof address === 'string' ? address : undefined),
    };
  }

  public addressAsObject(address: AddressDto | string | undefined): AddressDto | undefined {
    return typeof address === 'object' && address !== null ? address : undefined;
  }

  public addressToLabel(address: AddressDto | string | undefined): string {
    if (!address) {
      return '';
    }
    if (typeof address === 'string') {
      return address;
    }
    const line = [address.street, address.houseNumber].filter(Boolean).join(' ').trim();
    const city = address.city || address.town || address.village;
    return [line, address.postcode, city, address.province, address.country]
      .filter(Boolean)
      .join(', ');
  }

  public locationTitle(location?: GeoLocation): string {
    if (!location) {
      return GeoLocationService.UNDEFINED_LOCATION_LABEL;
    }
    const addressLabel = this.addressToLabel(location.address);
    return (
      location.formalName ||
      location.displayName ||
      addressLabel ||
      GeoLocationService.UNDEFINED_LOCATION_LABEL
    );
  }

  public locationSubtitle(location?: GeoLocation): string {
    if (!location) {
      return '';
    }

    const address = this.addressAsObject(location.address);
    const streetLine = [
      location.street ?? address?.street,
      location.houseNumber ?? address?.houseNumber,
    ]
      .filter(Boolean)
      .join(' ')
      .trim();
    const cityLine = (
      location.city ||
      location.town ||
      location.village ||
      address?.city ||
      address?.town ||
      address?.village ||
      ''
    ).trim();
    const parts = [
      streetLine,
      location.postcode ?? address?.postcode,
      cityLine,
      location.province ?? address?.province,
      location.country ?? address?.country,
    ].filter((value) => !!value && `${value}`.trim() !== '');

    if (parts.length) {
      return parts.join(', ');
    }

    return this.addressToLabel(location.address) || location.rawInput || '';
  }

  public locationCityAndProvince(location?: GeoLocation): string {
    if (!location) {
      return '';
    }

    const address = this.addressAsObject(location.address);
    const city = (
      location.city ||
      location.town ||
      location.village ||
      address?.city ||
      address?.town ||
      address?.village ||
      ''
    ).trim();
    const province = (location.province || address?.province || '').trim();

    if (city && province && city.toLowerCase() !== province.toLowerCase()) {
      return `${city} (${province})`;
    }
    if (city) {
      return city;
    }
    if (province) {
      return province;
    }
    return typeof location.address === 'string' ? location.address.trim() : '';
  }

  public locationQuery(location?: GeoLocation): string {
    const title = this.locationTitle(location);
    const validTitle = title !== GeoLocationService.UNDEFINED_LOCATION_LABEL ? title : '';

    const subtitle = this.locationSubtitle(location);

    if (validTitle && subtitle && validTitle !== subtitle) {
      return `${validTitle}, ${subtitle}`;
    }

    if (validTitle) {
      return validTitle;
    }

    if (subtitle) {
      return subtitle;
    }

    const addressLabel = this.addressToLabel(location?.address);
    if (addressLabel) {
      return addressLabel;
    }

    const rawInput = (location?.rawInput || '').trim();
    if (rawInput) {
      return rawInput;
    }

    return '';
  }

  public mapsLinkLabel(location?: GeoLocation): string {
    if (!location) {
      return '';
    }

    const formalName = (location.formalName || '').trim();
    const subtitle = this.locationSubtitle(location);
    const title = this.locationTitle(location);
    const fallback = title === GeoLocationService.UNDEFINED_LOCATION_LABEL ? '' : title;
    const placeDetails = subtitle || fallback;

    if (formalName && placeDetails && formalName !== placeDetails) {
      return `${formalName} - ${placeDetails}`;
    }

    return formalName || placeDetails || '';
  }

  public coordinates(location?: GeoLocation): GeoCoordinates | null {
    return parseCoordinates(location);
  }

  public mapsUrl(location?: GeoLocation): string {
    if (!location) {
      return '';
    }

    const lat = location.lat || (location.latitude != null ? `${location.latitude}` : '');
    const lon = location.lon || (location.longitude != null ? `${location.longitude}` : '');
    if (lat && lon) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lat},${lon}`)}`;
    }

    const query = this.locationQuery(location);
    return query
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
      : '';
  }
}

function emptyToUndefined(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}
